import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Database, Search, X } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { useApp } from "@/context/AppContext";
import POKEMON_SETS_INDEX from "@/data/pokemonSetsIndex.json";

export const Route = createFileRoute("/tools/setmanager")({
  component: SetManagerPage,
});

interface IndexedSet {
  id: string;
  name: string;
  series?: string;
  releaseDate?: string;
  ptcgoCode?: string;
}

const SETS = POKEMON_SETS_INDEX as IndexedSet[];

// Group by series, sort series by their most-recent set's release date desc,
// sort sets within a series desc.
interface SeriesGroup {
  series: string;
  latest: string;
  sets: IndexedSet[];
}
function buildGroups(sets: IndexedSet[]): SeriesGroup[] {
  const map = new Map<string, IndexedSet[]>();
  for (const s of sets) {
    const key = s.series?.trim() || "Other";
    const bucket = map.get(key) ?? [];
    bucket.push(s);
    map.set(key, bucket);
  }
  const groups: SeriesGroup[] = Array.from(map.entries()).map(
    ([series, sets]) => {
      const sorted = [...sets].sort((a, b) =>
        (b.releaseDate ?? "").localeCompare(a.releaseDate ?? ""),
      );
      return { series, latest: sorted[0]?.releaseDate ?? "", sets: sorted };
    },
  );
  groups.sort((a, b) => b.latest.localeCompare(a.latest));
  return groups;
}

function formatDate(iso?: string): string {
  if (!iso) return "";
  try {
    return new Date(iso.replace(/\//g, "-")).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function SetManagerPage() {
  const { cards } = useApp();

  // ── Coverage: setId/name(lower) → owned-card count ────────────────────
  // Walk cards once; index by both setCode (id) and setName (case-insensitive).
  const coverage = useMemo(() => {
    const byId = new Map<string, number>();
    const byName = new Map<string, number>();
    for (const c of cards) {
      const qty = c.quantity ?? 1;
      const id = c.setCode?.trim().toLowerCase();
      if (id) byId.set(id, (byId.get(id) ?? 0) + qty);
      const name = c.setName?.trim().toLowerCase();
      if (name) byName.set(name, (byName.get(name) ?? 0) + qty);
    }
    return { byId, byName };
  }, [cards]);

  const setOwnedCount = (s: IndexedSet): number => {
    const byId = coverage.byId.get(s.id.toLowerCase()) ?? 0;
    if (byId > 0) return byId;
    return coverage.byName.get(s.name.toLowerCase()) ?? 0;
  };

  const [search, setSearch] = useState("");
  const [ownedOnly, setOwnedOnly] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return SETS.filter((s) => {
      if (
        q &&
        !s.name.toLowerCase().includes(q) &&
        !s.id.toLowerCase().includes(q) &&
        !(s.series ?? "").toLowerCase().includes(q) &&
        !(s.ptcgoCode ?? "").toLowerCase().includes(q)
      ) {
        return false;
      }
      if (ownedOnly && setOwnedCount(s) === 0) return false;
      return true;
    });
  }, [search, ownedOnly, coverage]);

  const groups = useMemo(() => buildGroups(filtered), [filtered]);

  const totalSets = SETS.length;
  const visibleSets = filtered.length;
  const ownedSets = useMemo(
    () => SETS.filter((s) => setOwnedCount(s) > 0).length,
    [coverage],
  );

  const toggleGroup = (series: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(series)) next.delete(series);
      else next.add(series);
      return next;
    });
  };

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">Set Database</h1>
        <p className="text-sm text-muted-foreground">
          Browse the Pokémon TCG catalog ({totalSets} sets). Use the search to
          find a set by name, series, or PTCGO code. Owned counts come from your
          inventory.
        </p>
      </div>

      <div className="flex flex-col gap-2 border-b border-border py-3">
        <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by name, series, id, or PTCGO code…"
            className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <div>
            {visibleSets} of {totalSets} sets · {ownedSets} represented in
            inventory
          </div>
          <label className="inline-flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={ownedOnly}
              onChange={(e) => setOwnedOnly(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-border"
            />
            <span>Owned only</span>
          </label>
        </div>
      </div>

      <div
        className="flex-1 space-y-3 overflow-y-auto py-4 pr-1"
        style={{ scrollbarGutter: "stable" }}
      >
        {groups.length === 0 && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-12 text-center text-sm text-muted-foreground">
            <Database className="h-7 w-7" />
            <div>No sets match the current filter.</div>
          </div>
        )}

        {groups.map((g) => {
          const isCollapsed = collapsed.has(g.series);
          return (
            <div
              key={g.series}
              className="overflow-hidden rounded-md border border-border bg-card"
            >
              <button
                type="button"
                onClick={() => toggleGroup(g.series)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/40"
              >
                {isCollapsed ? (
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                <div className="text-sm font-semibold text-foreground">
                  {g.series}
                </div>
                <div className="text-xs text-muted-foreground">
                  · {g.sets.length} set{g.sets.length !== 1 ? "s" : ""}
                </div>
              </button>

              {!isCollapsed && (
                <div className="divide-y divide-border border-t border-border">
                  {g.sets.map((s) => {
                    const owned = setOwnedCount(s);
                    return (
                      <Link
                        key={s.id}
                        to="/inventory"
                        search={{ q: s.name, family: undefined }}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-muted/40"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <div className="truncate text-sm font-semibold text-foreground">
                              {s.name}
                            </div>
                            {s.ptcgoCode && (
                              <span className="inline-flex items-center rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                                {s.ptcgoCode}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {formatDate(s.releaseDate)}
                            <span className="ml-1 text-muted-foreground/60">
                              · {s.id}
                            </span>
                          </div>
                        </div>
                        {owned > 0 ? (
                          <div className="inline-flex shrink-0 items-center rounded bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">
                            {owned} owned
                          </div>
                        ) : (
                          <div className="shrink-0 text-[11px] text-muted-foreground/60">
                            —
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
